import React from 'react';
import Chart from 'react-apexcharts';
import { Box, Typography, Card, CardContent } from '@mui/material';

/**
 * BaseChart Component
 *
 * A reusable chart component that provides consistent styling and configuration
 * across all charts in the dashboard.
 */
const BaseChart = ({
  title,
  subtitle,
  type,
  series,
  options = {},
  height = 400,
  loading = false,
  children,
  ...props
}) => {
  // Default chart options that apply to all charts
  const defaultOptions = {
    chart: {
      type,
      height,
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
      },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
      spacing: type === 'pie' ? [0, 0, 0, 0] : [0, 0, 0, 0],
      offsetX: 0,
      offsetY: 0,
      width: '100%',
      parentHeightOffset: 0,
      ...(type === 'pie' && {
        width: '100%',
      }),
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
      padding: {
        top: 5,
        right: 0,
        bottom: 5,
        left: 0,
      },
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    tooltip: {
      enabled: true,
      shared: false,
      followCursor: true,
      intersect: false,
    },
    // Common plot options for bar charts
    plotOptions: {
      bar: {
        horizontal: false,
        barHeight: '50%',
        borderRadius: 4,
        dataLabels: {
          position: 'center',
        },
        columnWidth: '60%',
        distributed: true,
      },
    },
    // Common data labels configuration
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '10px',
        fontWeight: 'bold',
        colors: ['#333'],
      },
      offsetX: 0,
      offsetY: 0,
    },
    // Common axis configuration
    xaxis: {
      labels: {
        style: {
          fontSize: '10px',
        },
        rotate: -45,
        maxHeight: 80,
        trim: true,
        hideOverlappingLabels: true,
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: '11px',
        },
        formatter: function (val) {
          return val ? val.toLocaleString() : '0';
        },
      },
    },
    // Common legend configuration
    legend: {
      position: 'right',
      horizontalAlign: 'right',
      verticalAlign: 'top',
      fontSize: '12px',
      fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
      labels: {
        colors: '#333',
      },
      markers: {
        width: 12,
        height: 12,
        strokeWidth: 0,
        radius: 2,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 8,
      },
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: height * 0.75, // 75% of original height on mobile
          },
          dataLabels: {
            enabled: false,
          },
          legend: {
            position: 'left',
            horizontalAlign: 'left',
            fontSize: '10px',
          },
          xaxis: {
            labels: {
              rotate: -90,
              style: {
                fontSize: '9px',
              },
            },
          },
        },
      },
    ],
  };

  // Deep merge custom options with defaults, preserving nested objects
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    // Deep merge plotOptions to preserve BaseChart bar settings
    plotOptions: {
      ...defaultOptions.plotOptions,
      ...options.plotOptions,
      bar: {
        ...defaultOptions.plotOptions.bar,
        ...options.plotOptions?.bar,
      },
    },
    // Deep merge dataLabels
    dataLabels: {
      ...defaultOptions.dataLabels,
      ...options.dataLabels,
    },
    // Deep merge xaxis
    xaxis: {
      ...defaultOptions.xaxis,
      ...options.xaxis,
      labels: {
        ...defaultOptions.xaxis.labels,
        ...options.xaxis?.labels,
      },
    },
    // Deep merge yaxis
    yaxis: {
      ...defaultOptions.yaxis,
      ...options.yaxis,
      labels: {
        ...defaultOptions.yaxis.labels,
        ...options.yaxis?.labels,
      },
    },
    // Deep merge legend
    legend: {
      ...defaultOptions.legend,
      ...options.legend,
      labels: {
        ...defaultOptions.legend.labels,
        ...options.legend?.labels,
      },
      markers: {
        ...defaultOptions.legend.markers,
        ...options.legend?.markers,
      },
    },
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={height}
            color="text.secondary"
          >
            Loading...
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 2,
          width: '100%',
          minHeight: 0,
          '&:last-child': { pb: 2 },
        }}
      >
        <Box sx={{ mb: 1, flexShrink: 0 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Custom content before chart */}
        {children}

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            minHeight: 0,
            position: 'relative',
            ...(type === 'pie'
              ? {
                  justifyContent: 'center',
                  alignItems: 'center',
                  '& .apexcharts-canvas': {
                    margin: '0 auto',
                  },
                  '& svg': {
                    overflow: 'visible',
                  },
                }
              : {
                  '& > div': {
                    width: '100% !important',
                    flex: 1,
                  },
                  '& .apexcharts-canvas': {
                    width: '100% !important',
                    maxWidth: '100% !important',
                  },
                  '& .apexcharts-inner': {
                    width: '100% !important',
                  },
                  '& .apexcharts-svg': {
                    width: '100% !important',
                  },
                }),
          }}
        >
          <Chart
            options={mergedOptions}
            series={series}
            type={type}
            height={height}
            width="100%"
            {...props}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default BaseChart;
